/**
 * Markdown rendering for activity descriptions.
 *
 * Pipeline: marked (CommonMark + GFM) → DOMPurify (strict allowlist).
 *
 * Why this shape:
 *   - Client-side only. Server stores raw markdown source — that's the
 *     canonical form. For private rows the source lives inside the
 *     encrypted payload; we decrypt then render here.
 *   - Allowlist over blocklist. The default DOMPurify config strips
 *     `javascript:` etc, but we tighten further: no images (external
 *     image URLs leak the viewer's IP), no raw HTML pass-through, only
 *     http/https/mailto in links.
 *   - All links get `target="_blank" rel="noopener noreferrer ugc"`
 *     via a DOMPurify hook. We treat description content as user-
 *     generated, untrusted text — the rel attributes match the
 *     existing external-link pattern in PublicList.svelte.
 *
 * The hook registration runs at module load; safe because DOMPurify
 * hooks are global to the DOMPurify instance and idempotent.
 */
import { marked } from 'marked';
import DOMPurify from 'dompurify';

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer ugc');
  }
});

const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'strong', 'em', 'del', 's',
  'code', 'pre',
  'ul', 'ol', 'li',
  'blockquote',
  'a',
  // Headings deliberately limited to h3-h6 — h1/h2 would compete with the
  // SPA's own heading hierarchy. Markdown's `#` and `##` get downshifted by
  // marked's `headerIds: false` config plus our renderer override below.
  'h3', 'h4', 'h5', 'h6',
];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

// Configure marked once. `breaks: true` makes a single newline render as
// <br>, which matches the existing pre-wrap behaviour users are used to.
// `gfm: true` enables strikethrough + autolinks + tables, but tables are
// stripped at the DOMPurify layer (not in the allowlist).
marked.use({
  gfm: true,
  breaks: true,
  // Downshift h1/h2 to h3 to avoid colliding with the SPA's <h1>/<h2>.
  // h3..h6 pass through unchanged. walkTokens mutates the depth before
  // the default heading renderer runs, which gets us the level shift
  // without having to re-implement inline parsing in a custom renderer.
  walkTokens(token) {
    if (token.type === 'heading' && token.depth <= 2) {
      token.depth = 3;
    }
  },
});

/**
 * Render markdown source to sanitised HTML.
 *
 * Returns an empty string for empty/whitespace-only input so callers can
 * cheaply check whether there's anything to display.
 */
export function renderMarkdown(src: string | null | undefined): string {
  if (!src || !src.trim()) return '';
  const html = marked.parse(src, { async: false }) as string;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Only http(s) and mailto. Default DOMPurify also strips javascript:
    // but being explicit avoids surprises on protocol additions.
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // KEEP_CONTENT defaults to true: text content of STRIPPED tags is
    // preserved as plain text (so `<script>alert(1)</script>` becomes the
    // harmless string `alert(1)`). Script-type tag CONTENTS are still
    // stripped via DOMPurify's internal FORBID_CONTENTS list. Setting
    // KEEP_CONTENT:false was inexplicably wiping content of *allowed* tags
    // too — confirmed via tests/_probe — so the default wins here.
  });
}
